import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DocumentPreview from '../components/GatewaySessionModal/DocumentPreview';
import MessagesPanel from '../components/MessagesPanel';
import './GatewaySession.css';

const API_BASE_URL = '/api';

// Check if running in Electron
const isElectron = !!(
  window.electronAPI?.isElectron ||
  navigator.userAgent.toLowerCase().includes('electron') ||
  window.process?.type === 'renderer'
);

console.log('isElectron:', isElectron, 'userAgent:', navigator.userAgent);

const GatewaySession = ({ applicationId: propApplicationId, onClose }) => {
  const params = useParams();
  const navigate = useNavigate();
  const applicationId = propApplicationId || params.applicationId;
  const isModal = !!onClose;

  const handleBack = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/command-center');
    }
  };
  const webviewRef = useRef(null);
  const urlInputRef = useRef(null);

  const [applicationData, setApplicationData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Preview state
  const [previewDoc, setPreviewDoc] = useState(null);

  // Webview state (Electron only)
  const [webviewLoading, setWebviewLoading] = useState(true);
  const [currentUrl, setCurrentUrl] = useState('');
  const [urlInputValue, setUrlInputValue] = useState('');

  // Vault picker state
  const [showVaultPicker, setShowVaultPicker] = useState(false);
  const [pendingSelectionId, setPendingSelectionId] = useState(null);
  const [selectingDoc, setSelectingDoc] = useState(null);

  // Finalize state
  const [showFinalizePopup, setShowFinalizePopup] = useState(false);
  const [externalAppNumber, setExternalAppNumber] = useState('');
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeError, setFinalizeError] = useState('');

  // Status dropdown state
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Sidebar tab state
  const [activeTab, setActiveTab] = useState('details');

  // Messages panel state
  const [showMessages, setShowMessages] = useState(false);
  const [hasWhatsapp, setHasWhatsapp] = useState(null);

  // Accordion state for extracted data
  const [expandedAccordions, setExpandedAccordions] = useState({});

  // Visa upload state
  const [showVisaUpload, setShowVisaUpload] = useState(false);
  const [visaFile, setVisaFile] = useState(null);
  const [uploadingVisa, setUploadingVisa] = useState(false);
  const [visaUploadSuccess, setVisaUploadSuccess] = useState(false);

  // WhatsApp confirmation state
  const [statusConfirm, setStatusConfirm] = useState(null); // { newStatus, currentStatus }
  const [waConfirm, setWaConfirm] = useState(null); // { newStatus }

  // Listen for file picker interception from Electron main process
  useEffect(() => {
    if (!isElectron || !window.electronAPI?.onShowVaultPicker) return;

    // When main process detects file input click, show our vault
    window.electronAPI.onShowVaultPicker((data) => {
      console.log('Show vault picker requested:', data);
      setPendingSelectionId(data.selectionId);
      setShowVaultPicker(true);
    });

    return () => {
      if (window.electronAPI?.removeShowVaultPickerListener) {
        window.electronAPI.removeShowVaultPickerListener();
      }
    };
  }, [isElectron]);

  // Setup webview event listeners (Electron only)
  useEffect(() => {
    if (!isElectron) return;

    // Wait for webview to be in DOM
    const setupWebview = () => {
      const webview = webviewRef.current;
      if (!webview) return;

      const handleStartLoading = () => setWebviewLoading(true);
      const handleStopLoading = () => setWebviewLoading(false);
      const handleFailLoad = (e) => {
        console.error('Webview failed to load:', e);
        setWebviewLoading(false);
      };
      const handleDidNavigate = (e) => {
        console.log('Navigated to:', e.url);
        setCurrentUrl(e.url);
        setUrlInputValue(e.url);
      };
      const handleDomReady = () => {
        console.log('Webview DOM ready');
        setWebviewLoading(false);
      };

      webview.addEventListener('did-start-loading', handleStartLoading);
      webview.addEventListener('did-stop-loading', handleStopLoading);
      webview.addEventListener('did-fail-load', handleFailLoad);
      webview.addEventListener('did-navigate', handleDidNavigate);
      webview.addEventListener('did-navigate-in-page', handleDidNavigate);
      webview.addEventListener('dom-ready', handleDomReady);

      return () => {
        webview.removeEventListener('did-start-loading', handleStartLoading);
        webview.removeEventListener('did-stop-loading', handleStopLoading);
        webview.removeEventListener('did-fail-load', handleFailLoad);
        webview.removeEventListener('did-navigate', handleDidNavigate);
        webview.removeEventListener('did-navigate-in-page', handleDidNavigate);
        webview.removeEventListener('dom-ready', handleDomReady);
      };
    };

    // Small delay to ensure webview is mounted
    const timer = setTimeout(setupWebview, 100);
    return () => clearTimeout(timer);
  }, [isElectron, applicationData]);

  // Fetch application data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [appRes, docsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/gateway/applications/${applicationId}`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/gateway/applications/${applicationId}/documents`, { credentials: 'include' })
        ]);

        if (!appRes.ok || !docsRes.ok) {
          throw new Error('Failed to fetch data');
        }

        const appData = await appRes.json();
        const docsData = await docsRes.json();

        console.log('Gateway App Data:', appData);
        console.log('Gateway Docs Data:', docsData);

        if (appData.success) {
          setApplicationData(appData.data);
          // Set initial URL
          const portalUrl = appData.data?.visa?.portalUrl || 'https://google.com';
          setCurrentUrl(portalUrl);
          setUrlInputValue(portalUrl);
        }
        if (docsData.success) {
          console.log('Documents received:', docsData.data.documents);
          console.log('Required documents:', docsData.data.requiredDocuments);
          setDocuments(docsData.data.documents || []);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [applicationId]);

  // Poll documents & extracted data every 10s to catch WhatsApp uploads processed in background
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const [docsRes, appRes] = await Promise.all([
          fetch(`${API_BASE_URL}/gateway/applications/${applicationId}/documents`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/gateway/applications/${applicationId}`, { credentials: 'include' })
        ]);
        if (docsRes.ok) {
          const docsData = await docsRes.json();
          if (docsData.success) {
            setDocuments(docsData.data.documents || []);
          }
        }
        if (appRes.ok) {
          const appData = await appRes.json();
          if (appData.success) {
            // Update extracted data without overwriting portal URL state
            setApplicationData(prev => prev ? { ...prev, extractedData: appData.data.extractedData } : prev);
          }
        }
      } catch (_) {}
    }, 10000);

    return () => clearInterval(interval);
  }, [applicationId]);

  // Fetch hasWhatsapp for contact channel buttons
  useEffect(() => {
    const fetchChannel = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/conversations/${applicationId}`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) {
          setHasWhatsapp(data.data.hasWhatsapp);
        }
      } catch (_) {}
    };
    fetchChannel();
  }, [applicationId]);

  // Navigate to URL
  const handleNavigate = (url) => {
    if (!url) return;

    // Add protocol if missing
    let finalUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      finalUrl = 'https://' + url;
    }

    setCurrentUrl(finalUrl);
    setUrlInputValue(finalUrl);

    if (webviewRef.current) {
      webviewRef.current.src = finalUrl;
    }
  };

  // Handle URL input submit
  const handleUrlSubmit = (e) => {
    e.preventDefault();
    handleNavigate(urlInputValue);
    urlInputRef.current?.blur();
  };

  // Preview document
  const handlePreviewDocument = async (doc) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/gateway/documents/${doc.id}/preview?applicationId=${applicationId}`,
        { credentials: 'include' }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setPreviewDoc({
          ...data.data,
          typeName: doc.typeName
        });
      }
    } catch (err) {
      console.error('Preview error:', err);
    }
  };

  // Download document
  const handleDownloadDocument = async (doc) => {
    try {
      const res = await fetch(`${API_BASE_URL}/gateway/documents/${doc.id}/download?applicationId=${applicationId}`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition');
      const filename = disposition?.match(/filename="(.+)"/)?.[1] || doc.typeName || 'document';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  // Select document from vault for upload (when file picker is intercepted)
  const handleSelectDocument = async (doc) => {
    try {
      setSelectingDoc(doc.id);

      // Fetch the actual file from backend
      const response = await fetch(`${API_BASE_URL}/gateway/documents/${doc.id}/prepare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ applicationId })
      });

      if (!response.ok) throw new Error('Failed to get document');

      const data = await response.json();

      if (data.success) {
        // If we have a pending file selection from Electron
        if (pendingSelectionId && window.electronAPI?.vaultFileSelected) {
          // Send file to Electron main process
          const result = await window.electronAPI.vaultFileSelected(
            data.data.file, // base64 file data
            data.data.filename,
            data.data.mimeType,
            pendingSelectionId
          );

          console.log('Vault file selection result:', result);

          // Close vault picker
          setShowVaultPicker(false);
          setPendingSelectionId(null);
        }
      }
    } catch (err) {
      console.error('Select document error:', err);
    } finally {
      setSelectingDoc(null);
    }
  };

  // Cancel vault picker (when user closes without selecting)
  const handleCancelVaultPicker = () => {
    if (pendingSelectionId && window.electronAPI?.vaultFileCancelled) {
      window.electronAPI.vaultFileCancelled(pendingSelectionId);
    }
    setShowVaultPicker(false);
    setPendingSelectionId(null);
  };

  // Handle finalize application
  const handleFinalize = async () => {
    if (!externalAppNumber.trim()) {
      setFinalizeError('Please enter the external application number');
      return;
    }

    try {
      setFinalizing(true);
      setFinalizeError('');

      const response = await fetch(`${API_BASE_URL}/gateway/applications/${applicationId}/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ externalApplicationNumber: externalAppNumber.trim() })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to finalize application');
      }

      // Success - close popup and navigate to status center
      setShowFinalizePopup(false);
      handleBack();
    } catch (err) {
      setFinalizeError(err.message);
    } finally {
      setFinalizing(false);
    }
  };

  // Step 1: Dropdown click → show status confirmation
  const requestStatusChange = (newStatus) => {
    setShowStatusDropdown(false);
    setStatusConfirm({ newStatus, currentStatus });
  };

  // Step 2: Status confirmed → if approved/rejected show WhatsApp modal, else execute
  const confirmStatusChange = () => {
    const { newStatus } = statusConfirm;
    setStatusConfirm(null);
    if (newStatus === 'approved' || newStatus === 'rejected') {
      setWaConfirm({ newStatus });
    } else {
      executeStatusChange(newStatus, false);
    }
  };

  // Step 3: Execute the actual API call
  const executeStatusChange = async (newStatus, sendWhatsApp = false) => {
    try {
      setUpdatingStatus(true);

      const response = await fetch(`${API_BASE_URL}/gateway/applications/${applicationId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus, sendWhatsApp })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update status');
      }

      // Update local state
      setApplicationData(prev => ({
        ...prev,
        application: {
          ...prev.application,
          status: newStatus
        }
      }));
    } catch (err) {
      console.error('Status update error:', err);
      alert('Failed to update status: ' + err.message);
    } finally {
      setUpdatingStatus(false);
      setWaConfirm(null);
    }
  };

  // Handle visa document upload
  const handleVisaUpload = async () => {
    if (!visaFile) return;
    try {
      setUploadingVisa(true);
      const formData = new FormData();
      formData.append('visa', visaFile);

      const response = await fetch(`${API_BASE_URL}/gateway/applications/${applicationId}/upload-visa`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload visa');
      }

      setVisaUploadSuccess(true);
      setVisaFile(null);

      // Refresh documents list
      const docsRes = await fetch(`${API_BASE_URL}/gateway/applications/${applicationId}/documents`, { credentials: 'include' });
      if (docsRes.ok) {
        const docsData = await docsRes.json();
        if (docsData.success) setDocuments(docsData.data.documents || []);
      }

      // Auto-close after 2 seconds
      setTimeout(() => {
        setShowVisaUpload(false);
        setVisaUploadSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Visa upload error:', err);
      alert('Failed to upload visa: ' + err.message);
    } finally {
      setUploadingVisa(false);
    }
  };

  // Available statuses for dropdown (matching StatusCenter design)
  const statusOptions = [
    { value: 'submitted', label: 'PENDING', className: 'status-pending' },
    { value: 'documents_required', label: 'DOCS REQUIRED', className: 'status-docs-required' },
    { value: 'in_review', label: 'IN REVIEW', className: 'status-in-review' },
    { value: 'processing', label: 'PROCESSING', className: 'status-processing' },
    { value: 'approved', label: 'APPROVED', className: 'status-approved' },
    { value: 'rejected', label: 'REJECTED', className: 'status-rejected' }
  ];

  // Get status config for current status
  const getStatusConfig = (status) => {
    return statusOptions.find(opt => opt.value === status) || statusOptions[0];
  };

  // Check if application is already finalized
  const isFinalized = applicationData?.application?.isFinalized;
  const currentStatus = applicationData?.application?.status;

  // Toggle accordion
  const toggleAccordion = (docCode) => {
    setExpandedAccordions(prev => ({
      ...prev,
      [docCode]: !prev[docCode]
    }));
  };

  if (loading) {
    return (
      <div className="gateway-session">
        <div className="gateway-loading">
          <div className="gateway-spinner"></div>
          <p>Loading Gateway Session...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gateway-session">
        <div className="gateway-error">
          <p>Error: {error}</p>
          <button onClick={handleBack}>Back</button>
        </div>
      </div>
    );
  }

  const portalUrl = applicationData?.visa?.portalUrl || 'https://google.com';

  // Electron layout - Full browser with always-visible sidebar
  if (isElectron) {
    return (
      <div className="gateway-electron">
        {/* Browser Toolbar */}
        <div className="browser-toolbar">
          <div className="nav-buttons">
            <button onClick={() => webviewRef.current?.goBack()} title="Back">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
            <button onClick={() => webviewRef.current?.goForward()} title="Forward">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
            <button onClick={() => webviewRef.current?.reload()} title="Reload">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M23 4v6h-6"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
            </button>
          </div>

          <form className="url-bar" onSubmit={handleUrlSubmit}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="2" y1="12" x2="22" y2="12"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
            <input
              ref={urlInputRef}
              type="text"
              value={urlInputValue}
              onChange={(e) => setUrlInputValue(e.target.value)}
              placeholder="Enter URL..."
              onFocus={(e) => e.target.select()}
            />
            {webviewLoading && <div className="url-loading"></div>}
          </form>

          {/* Right side toolbar actions */}
          <div className="toolbar-actions">
            {/* Status Dropdown - Pill style matching StatusCenter */}
            <div className="gateway-status-dropdown">
              <button
                className={`gateway-status-trigger ${getStatusConfig(currentStatus).className}`}
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                disabled={updatingStatus}
              >
                <span>{updatingStatus ? 'UPDATING...' : getStatusConfig(currentStatus).label}</span>
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {showStatusDropdown && (
                <div className="gateway-status-menu">
                  {statusOptions.map(option => (
                    <div
                      key={option.value}
                      className={`gateway-status-item ${currentStatus === option.value ? 'active' : ''}`}
                      onClick={() => currentStatus !== option.value && requestStatusChange(option.value)}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upload Visa Button - only when approved and visa not yet uploaded */}
            {currentStatus === 'approved' && !documents.some(d => d.typeCode === 'VISA') && (
              <button
                className="upload-visa-btn"
                onClick={() => setShowVisaUpload(true)}
                title="Upload Visa Document"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                Upload Visa
              </button>
            )}
            {currentStatus === 'approved' && documents.some(d => d.typeCode === 'VISA') && (
              <span className="visa-sent-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                Visa Sent
              </span>
            )}

            {/* Finalize & Exit Button - only show if not finalized */}
            {!isFinalized && (
              <button
                className="finalize-btn"
                onClick={() => setShowFinalizePopup(true)}
                title="Finalize Application"
              >
                Finalize & Exit
              </button>
            )}

            {/* Show "Already Finalized" badge if finalized */}
            {isFinalized && (
              <div className="finalized-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                In Review: {applicationData?.application?.externalApplicationNumber}
              </div>
            )}

            {/* Exit Button */}
            <button
              className="exit-btn"
              onClick={handleBack}
              title="Exit to Command Center"
            >
              Exit
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="browser-content">
          {/* Webview - Takes remaining space */}
          <div className="webview-wrapper">
            {webviewLoading && (
              <div className="webview-loading-overlay">
                <div className="gateway-spinner"></div>
                <p>Loading...</p>
              </div>
            )}
            <webview
              ref={webviewRef}
              src={currentUrl}
              className="full-webview"
              partition="persist:visa-portal"
              allowpopups="true"
            />
          </div>

          {/* Vault Sidebar - Always visible, but show different content if finalized */}
          <div className={`vault-sidebar ${pendingSelectionId ? 'vault-active' : ''} ${isFinalized ? 'vault-finalized' : ''}`}>
            {/* Applicant Info Header */}
            <div className="vault-applicant">
              <span className="label">Applicant</span>
              <span className="value">{applicationData?.applicant?.name}</span>
              <span className="app-id">{applicationData?.application?.applicationNumber}</span>
              <button
                className="contact-btn contact-btn-msg"
                onClick={() => setShowMessages(true)}
                title="Send message to applicant"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                Send Message
              </button>
            </div>

            {/* Messages Panel */}
            {showMessages && (
              <div className="applicant-messages-wrapper">
                <MessagesPanel
                  applicationId={applicationId}
                  onClose={() => setShowMessages(false)}
                />
              </div>
            )}

            {/* Show different content based on finalized status */}
            {isFinalized ? (
              <div className="vault-finalized-content">
                <div className="finalized-status">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  <h4>Application Submitted</h4>
                  <p>This application has been finalized and is now in review.</p>
                </div>

                <div className="finalized-details">
                  <div className="detail-item">
                    <span className="label">Status</span>
                    <span className="value status-badge">{applicationData?.application?.status}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">External App No.</span>
                    <span className="value">{applicationData?.application?.externalApplicationNumber || '-'}</span>
                  </div>
                </div>

                <div className="finalized-note">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="16" x2="12" y2="12"/>
                    <line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  <span>Documents are no longer accessible after finalization for security reasons.</span>
                </div>
              </div>
            ) : (
              <>
                {/* Tabs */}
                <div className="sidebar-tabs">
                  <button
                    className={`sidebar-tab ${activeTab === 'details' ? 'active' : ''}`}
                    onClick={() => setActiveTab('details')}
                  >
                    DETAILS
                  </button>
                  <button
                    className={`sidebar-tab ${activeTab === 'vault' ? 'active' : ''}`}
                    onClick={() => setActiveTab('vault')}
                  >
                    VAULT
                  </button>
                  {pendingSelectionId && (
                    <button onClick={handleCancelVaultPicker} title="Cancel Selection" className="cancel-selection-btn">
                      Cancel
                    </button>
                  )}
                </div>

                {/* Tab Content */}
                <div className="sidebar-tab-content">
                  {/* DETAILS Tab - Extracted Data with Accordions */}
                  {activeTab === 'details' && (
                    <div
                      className="details-tab-content no-select"
                      onContextMenu={(e) => e.preventDefault()}
                      onDragStart={(e) => e.preventDefault()}
                    >
                      {applicationData?.extractedData && Object.entries(applicationData.extractedData).filter(([code]) => code !== 'PHOTO').length > 0 ? (
                        Object.entries(applicationData.extractedData).filter(([code]) => code !== 'PHOTO').map(([docCode, docInfo]) => (
                          <div key={docCode} className="accordion-item">
                            <div
                              className={`accordion-header ${expandedAccordions[docCode] ? 'expanded' : ''}`}
                              onClick={() => toggleAccordion(docCode)}
                            >
                              <span className="accordion-title">{docInfo.documentType}</span>
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                className={`accordion-icon ${expandedAccordions[docCode] ? 'rotated' : ''}`}
                              >
                                <polyline points="6 9 12 15 18 9"/>
                              </svg>
                            </div>
                            {expandedAccordions[docCode] && (
                              <div className="accordion-content">
                                {Object.entries(docInfo.data || {}).map(([field, value]) => {
                                  if (value === null || value === undefined || typeof value === 'object') return null;
                                  const label = field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
                                  return (
                                    <div key={field} className="extracted-field">
                                      <span className="field-label">{label}</span>
                                      <span className="field-value" onCopy={(e) => e.preventDefault()}>{String(value)}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="no-extracted-data">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                            <line x1="16" y1="13" x2="8" y2="13"/>
                            <line x1="16" y1="17" x2="8" y2="17"/>
                          </svg>
                          <p>No extracted data available</p>
                          <span>Documents need to be processed first</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* VAULT Tab - Documents List */}
                  {activeTab === 'vault' && (
                    <div className="vault-tab-content">
                      {pendingSelectionId ? (
                        <p className="vault-hint vault-hint-active">Select a document to upload to the website</p>
                      ) : (
                        <p className="vault-hint">Click to preview. Documents will auto-select when website requests upload.</p>
                      )}

                      {documents.length === 0 ? (
                        <div className="no-documents">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                          </svg>
                          <p>No required documents found</p>
                          <span>Check visa configuration</span>
                        </div>
                      ) : (
                        documents.map(doc => (
                          <div
                            key={doc.id}
                            className={`vault-doc-item ${selectingDoc === doc.id ? 'selecting' : ''} ${pendingSelectionId ? 'selectable' : ''}`}
                            onClick={() => pendingSelectionId ? handleSelectDocument(doc) : handlePreviewDocument(doc)}
                          >
                            <div className="doc-icon">
                              {selectingDoc === doc.id ? (
                                <div className="doc-spinner"></div>
                              ) : doc.mimeType?.includes('pdf') ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                  <polyline points="14 2 14 8 20 8"/>
                                </svg>
                              ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                  <circle cx="8.5" cy="8.5" r="1.5"/>
                                  <polyline points="21 15 16 10 5 21"/>
                                </svg>
                              )}
                            </div>
                            <div className="doc-details">
                              <span className="doc-type">{doc.typeName}</span>
                              <span className="doc-name">{doc.filename}</span>
                              {doc.isMandatory && <span className="doc-required">Required</span>}
                            </div>
                            {pendingSelectionId ? (
                              <div className="select-indicator">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                  <polyline points="17 8 12 3 7 8"/>
                                  <line x1="12" y1="3" x2="12" y2="15"/>
                                </svg>
                              </div>
                            ) : (
                              <button
                                className="doc-download-btn"
                                title="Download"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    const res = await fetch(`${API_BASE_URL}/gateway/documents/${doc.id}/prepare`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      credentials: 'include',
                                      body: JSON.stringify({ applicationId })
                                    });
                                    const data = await res.json();
                                    if (data.success) {
                                      const byteChars = atob(data.data.file);
                                      const byteArray = new Uint8Array(byteChars.length);
                                      for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
                                      const blob = new Blob([byteArray], { type: data.data.mimeType });
                                      const url = window.URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = data.data.filename || doc.filename || 'document';
                                      document.body.appendChild(a);
                                      a.click();
                                      a.remove();
                                      window.URL.revokeObjectURL(url);
                                    }
                                  } catch (err) {
                                    console.error('Download error:', err);
                                  }
                                }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                  <polyline points="7 10 12 15 17 10"/>
                                  <line x1="12" y1="15" x2="12" y2="3"/>
                                </svg>
                              </button>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="vault-footer">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              <span>Secure document access</span>
            </div>
          </div>
        </div>

        {/* Document Preview Modal */}
        {previewDoc && (
          <DocumentPreview
            document={previewDoc}
            onClose={() => setPreviewDoc(null)}
          />
        )}

        {/* Finalize Popup */}
        {showFinalizePopup && (
          <div className="finalize-popup-overlay" onClick={() => !finalizing && setShowFinalizePopup(false)}>
            <div className="finalize-popup" onClick={(e) => e.stopPropagation()}>
              <div className="finalize-popup-header">
                <h3>Finalize Application</h3>
                <button
                  className="close-popup"
                  onClick={() => !finalizing && setShowFinalizePopup(false)}
                  disabled={finalizing}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>

              <div className="finalize-popup-content">
                <p className="finalize-description">
                  Enter the application number from the visa portal to complete this session.
                  After finalization, the application status will change to "In Review" and documents will no longer be accessible.
                </p>

                <div className="form-group">
                  <label htmlFor="externalAppNumber">External Application Number *</label>
                  <input
                    id="externalAppNumber"
                    type="text"
                    value={externalAppNumber}
                    onChange={(e) => setExternalAppNumber(e.target.value)}
                    placeholder="e.g., VIS-2024-123456"
                    disabled={finalizing}
                    autoFocus
                  />
                  <span className="form-hint">This is the reference number provided by the visa portal after submission</span>
                </div>

                {finalizeError && (
                  <div className="finalize-error">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="15" y1="9" x2="9" y2="15"/>
                      <line x1="9" y1="9" x2="15" y2="15"/>
                    </svg>
                    {finalizeError}
                  </div>
                )}
              </div>

              <div className="finalize-popup-actions">
                <button
                  className="btn-cancel"
                  onClick={() => setShowFinalizePopup(false)}
                  disabled={finalizing}
                >
                  Cancel
                </button>
                <button
                  className="btn-finalize"
                  onClick={handleFinalize}
                  disabled={finalizing || !externalAppNumber.trim()}
                >
                  {finalizing ? 'Finalizing...' : 'Finalize & Exit'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Status Change Confirmation Modal */}
        {statusConfirm && (
          <div className="finalize-popup-overlay" onClick={() => setStatusConfirm(null)}>
            <div className="finalize-popup wa-confirm-popup" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '360px', textAlign: 'center' }}>
              <div style={{ padding: '24px' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" style={{ marginBottom: '12px' }}>
                  <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 8px 0' }}>Change Application Status?</h3>
                <p style={{ fontSize: '13px', color: '#666', margin: '0 0 20px 0', lineHeight: 1.5 }}>
                  Change from <strong>{getStatusConfig(statusConfirm.currentStatus).label}</strong> to <strong>{getStatusConfig(statusConfirm.newStatus).label}</strong>?
                </p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => setStatusConfirm(null)}
                    style={{ flex: 1, padding: '10px 16px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmStatusChange}
                    style={{ flex: 1, padding: '10px 16px', background: '#2188C9', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: '#fff', cursor: 'pointer' }}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* WhatsApp Confirmation Modal */}
        {waConfirm && (
          <div className="finalize-popup-overlay" onClick={() => { executeStatusChange(waConfirm.newStatus, false); }}>
            <div className="finalize-popup wa-confirm-popup" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '360px', textAlign: 'center' }}>
              <div style={{ padding: '24px' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" style={{ marginBottom: '12px' }}>
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" fill="#25D366"/>
                  <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" stroke="#25D366" strokeWidth="1.5" fill="none"/>
                </svg>
                <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 8px 0' }}>Send WhatsApp Notification?</h3>
                <p style={{ fontSize: '13px', color: '#666', margin: '0 0 20px 0', lineHeight: 1.5 }}>
                  Notify <strong>{applicationData?.applicant?.name || 'applicant'}</strong> about visa {waConfirm.newStatus === 'approved' ? 'approval' : 'rejection'} via WhatsApp?
                </p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => executeStatusChange(waConfirm.newStatus, false)}
                    style={{ flex: 1, padding: '10px 16px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    No, Skip
                  </button>
                  <button
                    onClick={() => executeStatusChange(waConfirm.newStatus, true)}
                    style={{ flex: 1, padding: '10px 16px', background: '#25D366', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: '#fff', cursor: 'pointer' }}
                  >
                    Yes, Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Visa Upload Modal */}
        {showVisaUpload && (
          <div className="finalize-popup-overlay" onClick={() => !uploadingVisa && setShowVisaUpload(false)}>
            <div className="finalize-popup" onClick={(e) => e.stopPropagation()}>
              <div className="finalize-popup-header">
                <h3>Upload Visa Document</h3>
                <button
                  className="close-popup"
                  onClick={() => !uploadingVisa && setShowVisaUpload(false)}
                  disabled={uploadingVisa}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <div className="finalize-popup-content">
                {visaUploadSuccess ? (
                  <div className="visa-upload-success">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                    <p>Visa uploaded successfully!</p>
                    <span className="visa-upload-hint">Available in applicant's vault</span>
                  </div>
                ) : (
                  <>
                    <p className="finalize-description">
                      Upload the approved visa document. It will be stored in the applicant's vault with OCR extraction.
                    </p>
                    <div className="form-group">
                      <label htmlFor="visaFileInput">Visa Document (PDF, JPEG, PNG)</label>
                      <input
                        id="visaFileInput"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        onChange={(e) => setVisaFile(e.target.files[0] || null)}
                        disabled={uploadingVisa}
                        className="visa-file-input"
                      />
                      {visaFile && (
                        <span className="form-hint">{visaFile.name} ({(visaFile.size / 1024).toFixed(0)} KB)</span>
                      )}
                    </div>
                  </>
                )}
              </div>
              {!visaUploadSuccess && (
                <div className="finalize-popup-actions">
                  <button
                    className="btn-cancel"
                    onClick={() => { setShowVisaUpload(false); setVisaFile(null); }}
                    disabled={uploadingVisa}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-finalize"
                    onClick={handleVisaUpload}
                    disabled={uploadingVisa || !visaFile}
                  >
                    {uploadingVisa ? 'Uploading...' : 'Upload Visa'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Web layout - Original design with open in new tab
  return (
    <div className="gateway-session">
      {/* Header - only show when NOT inside drawer */}
      {!isModal && (
        <header className="gateway-header">
          <button className="back-button" onClick={handleBack}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back
          </button>
          <div className="gateway-title">
            <h1>GATEWAY SESSION</h1>
            <span className="application-id">{applicationData?.application?.applicationNumber}</span>
          </div>
          <div className="gateway-status">
            {currentStatus === 'approved' && !documents.some(d => d.typeCode === 'VISA') && (
              <button
                className="upload-visa-btn"
                onClick={() => setShowVisaUpload(true)}
                title="Upload Visa Document"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                Upload Visa
              </button>
            )}
            {currentStatus === 'approved' && documents.some(d => d.typeCode === 'VISA') && (
              <span className="visa-sent-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                Visa Sent
              </span>
            )}
            <span className="status-dot"></span>
            SECURE SESSION ACTIVE
          </div>
        </header>
      )}

      {/* Main Content */}
      <div className="gateway-content">
        <div className="documents-panel">
          {/* Applicant Info */}
          <div className="applicant-section">
            <h3>APPLICANT INFORMATION</h3>
            <div className="applicant-details">
              <div className="detail-row">
                <span className="label">FULL NAME</span>
                <span className="value">{applicationData?.applicant?.name || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="label">DATE OF BIRTH</span>
                <span className="value">
                  {applicationData?.applicant?.dateOfBirth
                    ? new Date(applicationData.applicant.dateOfBirth).toLocaleDateString('en-GB', {
                        day: 'numeric', month: 'short', year: 'numeric'
                      })
                    : '-'}
                </span>
              </div>
              <div className="detail-row">
                <span className="label">CONTACT</span>
                <div className="contact-buttons">
                  <button
                    className="contact-btn contact-btn-wa"
                    onClick={() => setShowMessages(true)}
                    disabled={hasWhatsapp === false}
                    title={hasWhatsapp === false ? 'No phone number available' : 'Send WhatsApp message'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </button>
                  <button
                    className="contact-btn contact-btn-email"
                    onClick={() => setShowMessages(true)}
                    disabled={hasWhatsapp === true}
                    title={hasWhatsapp === true ? 'WhatsApp available — use WhatsApp instead' : 'Send email message'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    Email
                  </button>
                </div>
              </div>
            </div>

            {/* Messages Panel (slides open) */}
            {showMessages && (
              <div className="applicant-messages-wrapper">
                <MessagesPanel
                  applicationId={applicationId}
                  onClose={() => setShowMessages(false)}
                />
              </div>
            )}

            <div className="visa-details">
              <h4>VISA DETAILS</h4>
              <div className="detail-row">
                <span className="label">VISA TYPE</span>
                <span className="value">{applicationData?.visa?.name || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="label">CATEGORY</span>
                <span className="value">{applicationData?.visa?.category || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="label">DESTINATION</span>
                <span className="value">
                  {applicationData?.visa?.destination?.flag} {applicationData?.visa?.destination?.name || '-'}
                </span>
              </div>
              {applicationData?.application?.travelDates?.start && (
                <div className="detail-row">
                  <span className="label">TRAVEL DATE</span>
                  <span className="value">
                    {new Date(applicationData.application.travelDates.start).toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'})}
                  </span>
                </div>
              )}
              {applicationData?.visa?.entries && (
                <div className="detail-row">
                  <span className="label">ENTRIES</span>
                  <span className="value" style={{textTransform: 'capitalize'}}>{applicationData.visa.entries}</span>
                </div>
              )}
              {applicationData?.visa?.validity && (
                <div className="detail-row">
                  <span className="label">VALIDITY</span>
                  <span className="value" style={{textTransform: 'capitalize'}}>{applicationData.visa.validity}</span>
                </div>
              )}
              {applicationData?.visa?.maxStay && (
                <div className="detail-row">
                  <span className="label">MAX STAY</span>
                  <span className="value" style={{textTransform: 'capitalize'}}>{applicationData.visa.maxStay}</span>
                </div>
              )}
              {applicationData?.visa?.processingDays && (
                <div className="detail-row">
                  <span className="label">PROCESSING</span>
                  <span className="value">
                    {applicationData.visa.processingDays.min === applicationData.visa.processingDays.max
                      ? `${applicationData.visa.processingDays.min} days`
                      : `${applicationData.visa.processingDays.min}-${applicationData.visa.processingDays.max} days`}
                  </span>
                </div>
              )}
              {applicationData?.visa?.notes && (
                <div className="detail-row">
                  <span className="label">NOTES</span>
                  <span className="value">{applicationData.visa.notes}</span>
                </div>
              )}
            </div>
          </div>

          {/* Extracted Data Section */}
          {applicationData?.extractedData && Object.entries(applicationData.extractedData).filter(([code]) => code !== 'PHOTO').length > 0 && (
            <div className="extracted-data-section">
              <h3>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
                EXTRACTED DATA
              </h3>
              <div className="extracted-data-accordions">
                {Object.entries(applicationData.extractedData).filter(([code]) => code !== 'PHOTO').map(([docCode, docInfo]) => (
                  <div key={docCode} className="accordion-item">
                    <div
                      className={`accordion-header ${expandedAccordions[docCode] ? 'expanded' : ''}`}
                      onClick={() => toggleAccordion(docCode)}
                    >
                      <span className="accordion-title">{docInfo.documentType}</span>
                      <svg
                        width="16" height="16" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2"
                        className={`accordion-icon ${expandedAccordions[docCode] ? 'rotated' : ''}`}
                      >
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </div>
                    {expandedAccordions[docCode] && (
                      <div className="accordion-content">
                        {Object.entries(docInfo.data || {}).map(([field, value]) => {
                          if (value === null || value === undefined || typeof value === 'object') return null;
                          const label = field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
                          return (
                            <div key={field} className="extracted-field">
                              <span className="field-label">{label}</span>
                              <span className="field-value">{String(value)}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Document Vault */}
          <div className="vault-section">
            <h3>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              DOCUMENT VAULT
            </h3>

            {/* Document List */}
            <div className="document-list">
              {documents.length === 0 ? (
                <div className="no-documents">
                  <p>No documents uploaded by applicant</p>
                </div>
              ) : (
                documents.map(doc => (
                  <div key={doc.id} className="document-item">
                    <div className="doc-icon">
                      {doc.mimeType?.includes('pdf') ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                          <line x1="16" y1="13" x2="8" y2="13"/>
                          <line x1="16" y1="17" x2="8" y2="17"/>
                          <polyline points="10 9 9 9 8 9"/>
                        </svg>
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                          <circle cx="8.5" cy="8.5" r="1.5"/>
                          <polyline points="21 15 16 10 5 21"/>
                        </svg>
                      )}
                    </div>
                    <div className="doc-info">
                      <span className="doc-name">{doc.typeName}</span>
                      <span className="doc-filename">{doc.filename}</span>
                    </div>
                    <div className="doc-actions">
                      <button
                        className="btn-preview"
                        onClick={() => handlePreviewDocument(doc)}
                        title="Preview"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      </button>
                      <button
                        className="btn-download"
                        onClick={() => handleDownloadDocument(doc)}
                        title="Download"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="7 10 12 15 17 10"/>
                          <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Security Notice */}
          <div className="security-notice">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            <span>Documents are watermarked for preview. Download the Desktop App for integrated portal access.</span>
          </div>
        </div>
      </div>

      {/* Document Preview Modal */}
      {previewDoc && (
        <DocumentPreview
          document={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}

      {/* Status Change Confirmation Modal */}
      {statusConfirm && (
        <div className="finalize-popup-overlay" onClick={() => setStatusConfirm(null)}>
          <div className="finalize-popup wa-confirm-popup" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '360px', textAlign: 'center' }}>
            <div style={{ padding: '24px' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" style={{ marginBottom: '12px' }}>
                <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 8px 0' }}>Change Application Status?</h3>
              <p style={{ fontSize: '13px', color: '#666', margin: '0 0 20px 0', lineHeight: 1.5 }}>
                Change from <strong>{getStatusConfig(statusConfirm.currentStatus).label}</strong> to <strong>{getStatusConfig(statusConfirm.newStatus).label}</strong>?
              </p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => setStatusConfirm(null)}
                  style={{ flex: 1, padding: '10px 16px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmStatusChange}
                  style={{ flex: 1, padding: '10px 16px', background: '#2188C9', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: '#fff', cursor: 'pointer' }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Confirmation Modal */}
      {waConfirm && (
        <div className="finalize-popup-overlay" onClick={() => { executeStatusChange(waConfirm.newStatus, false); }}>
          <div className="finalize-popup wa-confirm-popup" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '360px', textAlign: 'center' }}>
            <div style={{ padding: '24px' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" style={{ marginBottom: '12px' }}>
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" fill="#25D366"/>
                <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" stroke="#25D366" strokeWidth="1.5" fill="none"/>
              </svg>
              <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 8px 0' }}>Send WhatsApp Notification?</h3>
              <p style={{ fontSize: '13px', color: '#666', margin: '0 0 20px 0', lineHeight: 1.5 }}>
                Notify <strong>{applicationData?.applicant?.name || 'applicant'}</strong> about visa {waConfirm.newStatus === 'approved' ? 'approval' : 'rejection'} via WhatsApp?
              </p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => executeStatusChange(waConfirm.newStatus, false)}
                  style={{ flex: 1, padding: '10px 16px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                >
                  No, Skip
                </button>
                <button
                  onClick={() => executeStatusChange(waConfirm.newStatus, true)}
                  style={{ flex: 1, padding: '10px 16px', background: '#25D366', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: '#fff', cursor: 'pointer' }}
                >
                  Yes, Send
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Visa Upload Modal */}
      {showVisaUpload && (
        <div className="finalize-popup-overlay" onClick={() => !uploadingVisa && setShowVisaUpload(false)}>
          <div className="finalize-popup" onClick={(e) => e.stopPropagation()}>
            <div className="finalize-popup-header">
              <h3>Upload Visa Document</h3>
              <button
                className="close-popup"
                onClick={() => !uploadingVisa && setShowVisaUpload(false)}
                disabled={uploadingVisa}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="finalize-popup-content">
              {visaUploadSuccess ? (
                <div className="visa-upload-success">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  <p>Visa uploaded successfully!</p>
                  <span className="visa-upload-hint">Available in applicant's vault</span>
                </div>
              ) : (
                <>
                  <p className="finalize-description">
                    Upload the approved visa document. It will be stored in the applicant's vault with OCR extraction.
                  </p>
                  <div className="form-group">
                    <label htmlFor="visaFileInputWeb">Visa Document (PDF, JPEG, PNG)</label>
                    <input
                      id="visaFileInputWeb"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={(e) => setVisaFile(e.target.files[0] || null)}
                      disabled={uploadingVisa}
                      className="visa-file-input"
                    />
                    {visaFile && (
                      <span className="form-hint">{visaFile.name} ({(visaFile.size / 1024).toFixed(0)} KB)</span>
                    )}
                  </div>
                </>
              )}
            </div>
            {!visaUploadSuccess && (
              <div className="finalize-popup-actions">
                <button
                  className="btn-cancel"
                  onClick={() => { setShowVisaUpload(false); setVisaFile(null); }}
                  disabled={uploadingVisa}
                >
                  Cancel
                </button>
                <button
                  className="btn-finalize"
                  onClick={handleVisaUpload}
                  disabled={uploadingVisa || !visaFile}
                >
                  {uploadingVisa ? 'Uploading...' : 'Upload Visa'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GatewaySession;
